import { module } from "angular";
import { map } from "rxjs/operators";

import { forEach } from "lodash";

import type { Observable } from "rxjs";

import type { IResponse, ISiteSlim, ITeam, ITeamMember, ITeamSlim } from "App/Modules/Models";
import type { IRolesRepository } from "App/Modules/Repositories";
import type { IHttpService } from "App/Modules/Services";

import { SitesRepository } from "App/Modules/Repositories/Site/SitesRepository";
import type { ITeamsRepository } from "./ITeamsRepository";

export default class TeamsRepository implements ITeamsRepository {
    public static $inject: string[] = ["mmHttpService", "irRolesRepository"];

    constructor(private readonly httpService: IHttpService, private readonly rolesRepository: IRolesRepository) {}

    public getTeams(): Observable<IResponse<ITeamSlim[]>> {
        return this.httpService.get<ITeamSlim[]>("teams");
    }

    public getTeam(teamId: string): Observable<IResponse<ITeam>> {
        return this.httpService.get<ITeam>(`teams/${teamId}`).pipe(
            map((response: IResponse<ITeam>) => {
                if (!response.data) {
                    return response;
                }

                forEach(response.data.sites, (site) => {
                    SitesRepository.enhanceSite(site);
                });

                return response;
            }),
        );
    }

    public getTeamWithSites(teamId: string): Observable<IResponse<ISiteSlim[]>> {
        return this.httpService.get<ISiteSlim[]>(`teams/${teamId}/sites`).pipe(
            map((response: IResponse<ISiteSlim[]>) => {
                if (!response.data) {
                    return response;
                }

                forEach(response.data, (site) => {
                    SitesRepository.enhanceSite(site);
                });

                return response;
            }),
        );
    }

    public getTeamMembers(teamId: string): Observable<IResponse<ITeamMember[]>> {
        return this.httpService.get<ITeamMember[]>(`teams/${teamId}/users`).pipe(
            map((response: IResponse<ITeamMember[]>) => {
                if (!response.data) {
                    return response;
                }

                response.data.map((user) => {
                    if (user.role) {
                        user.role = this.rolesRepository.addTranslatedLabel(user.role);
                    } else {
                        user.role = this.rolesRepository.getNoRole();
                    }
                    return user;
                });

                return response;
            }),
        );
    }

    public addTeam(
        team: Partial<Omit<ITeamSlim, "name" | "partnerId">> & Pick<ITeamSlim, "name" | "partnerId">,
    ): Observable<IResponse<ITeamSlim>> {
        return this.httpService.post<ITeamSlim>("teams", team);
    }

    public addSite(teamId: string, site: ISiteSlim): Observable<IResponse<ISiteSlim>> {
        return this.httpService.post<ISiteSlim>(`teams/${teamId}/sites`, site).pipe(
            map((response: IResponse<ISiteSlim>) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceSite(response.data);
                return response;
            }),
        );
    }

    public addTeamMember(teamId: string, teamMember: ITeamMember): Observable<IResponse<ITeamMember>> {
        return this.httpService
            .post<ITeamMember>(`teams/${teamId}/users`, teamMember)
            .pipe(map((response: IResponse<ITeamMember>) => this.EnhanceTeamMemberResponse(response)));
    }

    public patchTeamMember(
        teamId: string,
        partnerUserId: string,
        teamMember: ITeamMember,
    ): Observable<IResponse<ITeamMember>> {
        return this.httpService
            .patch<ITeamMember>(`teams/${teamId}/users/${partnerUserId}`, teamMember)
            .pipe(map((response: IResponse<ITeamMember>) => this.EnhanceTeamMemberResponse(response)));
    }

    public deleteSite(teamId: string, siteId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`teams/${teamId}/sites/${siteId}`);
    }

    public deleteTeamMember(teamId: string, partnerUserId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`teams/${teamId}/users/${partnerUserId}`);
    }

    public EnhanceTeamMemberResponse(response: IResponse<ITeamMember>): IResponse<ITeamMember> {
        if (!response.data) {
            return response;
        }

        if (response.data.role) {
            response.data.role = this.rolesRepository.addTranslatedLabel(response.data.role);
        } else {
            response.data.role = this.rolesRepository.getNoRole();
        }
        return response;
    }
}

module("mmRepositories").service("mmTeamsRepository", TeamsRepository);
